import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
  getMedClipProjectName,
  isMedClipIntegrationDisabledRoute,
  isMedClipPageMessage,
  isMedClipProjectAllowed,
  isAuthoritativeMedClipMessage,
  isMedClipRecordingEvent,
  MEDCLIP_APP_ID,
  MEDCLIP_CANCELED_STUDY_TTL_MS,
  MEDCLIP_LAST_COMPLETED_TTL_MS,
} from "../utils/medclipConstants";

function logIncomingExtensionEvent(data, meta = {}) {
  const { type, event: eventName, source, success, error, errorCode, payload } =
    data;
  console.log(
    `[MedClip] ← ${type ?? "message"}: ${eventName ?? "(no event)"}`,
    {
      source: source ?? null,
      ...(type === "response"
        ? { success, errorCode: errorCode ?? null, error: error ?? null }
        : {}),
      payload: payload ?? null,
      ...meta,
    },
  );
}

function logOutgoingExtensionEvent(eventName, payload) {
  console.log(`[MedClip] → ${eventName}`, payload ?? null);
}

const IDLE_RECORDING_STATE = {
  isRecording: false,
  isPaused: false,
  isProcessing: false,
  isStarting: false,
  isLoading: false,
  error: null,
};

function pruneExpiredEntries(mapRef, ttlMs) {
  const now = Date.now();
  for (const [key, at] of mapRef.current.entries()) {
    if (now - at > ttlMs) {
      mapRef.current.delete(key);
    }
  }
}

/**
 * MedClip page-messaging integration (MEDCLIP_GUIDE.md).
 * CRE users only — do not use for Zikaron.
 */
export function useMedClipExtension({
  studyId,
  enabled = true,
  onRecordingComplete,
  t,
}) {
  const [isExtensionInstalled, setIsExtensionInstalled] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [isExtensionSectionDismissed, setIsExtensionSectionDismissed] =
    useState(false);
  const [recordingState, setRecordingState] = useState(IDLE_RECORDING_STATE);

  const recordingStateRef = useRef(recordingState);
  const loginStateRef = useRef({ timer: null, retryCount: 0 });
  const handledCompletionVidsRef = useRef(new Set());
  const recentlyCanceledStudyIdsRef = useRef(new Map());
  const sessionStudyIdRef = useRef(null);
  const studyIdRef = useRef(studyId);
  const authExpiredHandledRef = useRef(false);

  useEffect(() => {
    studyIdRef.current = studyId;
  }, [studyId]);

  const integrationActive =
    enabled && !isMedClipIntegrationDisabledRoute();

  useEffect(() => {
    recordingStateRef.current = recordingState;
  }, [recordingState]);

  const markStudyCanceled = useCallback((canceledStudyId) => {
    if (!canceledStudyId) return;
    pruneExpiredEntries(recentlyCanceledStudyIdsRef, MEDCLIP_CANCELED_STUDY_TTL_MS);
    recentlyCanceledStudyIdsRef.current.set(canceledStudyId, Date.now());
  }, []);

  const isStudyRecentlyCanceled = useCallback((sid) => {
    if (!sid) return false;
    pruneExpiredEntries(recentlyCanceledStudyIdsRef, MEDCLIP_CANCELED_STUDY_TTL_MS);
    return recentlyCanceledStudyIdsRef.current.has(sid);
  }, []);

  const shouldSwallowCompletion = useCallback(
    (payloadStudyId, vid) => {
      if (vid && handledCompletionVidsRef.current.has(vid)) return true;
      if (payloadStudyId && isStudyRecentlyCanceled(payloadStudyId)) return true;
      return false;
    },
    [isStudyRecentlyCanceled],
  );

  const isCompletionForCurrentStudy = useCallback((payloadStudyId) => {
    const current = studyIdRef.current;
    if (payloadStudyId && current && payloadStudyId !== current) return false;
    return true;
  }, []);

  const shouldApplyRecordingFlags = useCallback(() => {
    const current = studyIdRef.current;
    const session = sessionStudyIdRef.current;
    if (!session) return true;
    if (!current) return true;
    return session === current;
  }, []);

  const applyRecordingStateFromExtension = useCallback((rs) => {
    if (!rs) return;
    if (!shouldApplyRecordingFlags()) return;
    setRecordingState((prev) => ({
      ...prev,
      ...rs,
      error: rs.error ?? null,
    }));
  }, [shouldApplyRecordingFlags]);

  const handleRecoveredCompletion = useCallback(
    (rs) => {
      const vid = rs?.lastCompletedVid;
      const payloadStudyId = rs?.lastCompletedStudyId;
      const completedAt = rs?.lastCompletedAt;

      if (!vid || !payloadStudyId || !completedAt) return;
      if (Date.now() - completedAt > MEDCLIP_LAST_COMPLETED_TTL_MS) return;
      if (!isCompletionForCurrentStudy(payloadStudyId)) return;
      if (shouldSwallowCompletion(payloadStudyId, vid)) return;

      handledCompletionVidsRef.current.add(vid);
      onRecordingComplete?.({ vid, studyId: payloadStudyId });
    },
    [isCompletionForCurrentStudy, shouldSwallowCompletion, onRecordingComplete],
  );

  const postToExtension = useCallback((eventName, payload = undefined) => {
    if (!integrationActive) return;
    try {
      const message = {
        channel: "page",
        event: eventName,
        target: MEDCLIP_APP_ID,
      };
      if (payload !== undefined) {
        message.payload = payload;
      }
      logOutgoingExtensionEvent(eventName, payload);
      window.postMessage(message, window.location.origin);
    } catch {
      // no-op
    }
  }, [integrationActive]);

  const requestRecordingState = useCallback(() => {
    postToExtension("recording.state.get");
  }, [postToExtension]);

  const sendLoginRequest = useCallback(
    (delayMs = 500) => {
      if (!integrationActive) return;
      const state = loginStateRef.current;
      if (state.timer) {
        clearTimeout(state.timer);
        state.timer = null;
      }
      state.timer = setTimeout(() => {
        const projectName = getMedClipProjectName();
        postToExtension("login.request", { project: projectName });
        state.timer = null;
      }, delayMs);
    },
    [integrationActive, postToExtension],
  );

  const handleMedClipErrorCode = useCallback(
    (errorCode, errorMessage, { quiet = false } = {}) => {
      switch (errorCode) {
        case "REC_USER_CANCELED":
          return;
        case "AUTH_NO_STUDY":
          if (!quiet) {
            toast.info(t("authNoStudy") || errorMessage || "Select a study before recording.");
          }
          return;
        case "DESKTOP_APP_MISSING":
          if (!quiet) {
            toast.error(t("desktopAppMissing") || errorMessage || "Desktop helper app is not installed.");
          }
          return;
        case "REC_LIMIT_REACHED":
          if (!quiet) {
            toast.error(t("recordingLimitReached") || errorMessage || "Recording limit reached.");
          }
          return;
        case "REC_NO_RECORDING_IN_PROGRESS":
          return;
        case "SYS_CONTEXT_INVALIDATED":
          if (!quiet) {
            toast.error(t("extensionReloaded") || "Extension was updated. Please reload this page.");
          }
          return;
        case "AUTH_TOKEN_EXPIRED":
        case "API_UNAUTHORIZED":
          setIsExtensionInstalled(false);
          loginStateRef.current.retryCount = 0;
          if (loginStateRef.current.timer) {
            clearTimeout(loginStateRef.current.timer);
            loginStateRef.current.timer = null;
          }
          if (authExpiredHandledRef.current) return;
          authExpiredHandledRef.current = true;
          if (!quiet) {
            toast.error(
              t("authenticationExpired") ||
                "Authentication expired. Please log in again.",
            );
          }
          return;
        case "NET_OFFLINE":
          if (!quiet) {
            toast.error(t("noInternetConnection") || "No internet connection.");
          }
          return;
        case "API_SERVER_ERROR":
          if (!quiet) {
            toast.error(t("serverError") || "Server error. Please try again later.");
          }
          return;
        case "API_RATE_LIMIT":
          if (!quiet) {
            toast.error(t("rateLimitExceeded") || "Too many requests. Please try again later.");
          }
          return;
        default:
          if (!quiet && errorMessage) {
            toast.error(errorMessage);
          }
      }
    },
    [t],
  );

  const probeExtension = useCallback(() => {
    if (!integrationActive) return;
    if (document.body?.classList.contains("medclip-on")) {
      setIsExtensionInstalled(true);
    }
    postToExtension("project.get");
    requestRecordingState();
  }, [integrationActive, postToExtension, requestRecordingState]);

  // Message listener
  useEffect(() => {
    if (!integrationActive) return undefined;

    const handler = (event) => {
      const data = event?.data;
      if (!isMedClipPageMessage(event, data)) return;

      if (
        data.type === "response" &&
        data.source &&
        data.source !== MEDCLIP_APP_ID
      ) {
        logIncomingExtensionEvent(data, {
          action: "ignored",
          reason: `unexpected source: ${data.source}`,
        });
        return;
      }

      logIncomingExtensionEvent(data);

      const rejectNonMedClipRecording = (eventName) => {
        if (!isMedClipRecordingEvent(eventName)) return false;
        if (isAuthoritativeMedClipMessage(data)) return false;
        logIncomingExtensionEvent(data, {
          action: "ignored",
          reason:
            "recording event without source:medclip (likely Zikaron legacy response)",
        });
        return true;
      };

      if (data.type === "response") {
        const { event: eventName, success, error, errorCode, payload } = data;

        if (rejectNonMedClipRecording(eventName)) return;

        if (eventName === "project.get") {
          if (!isAuthoritativeMedClipMessage(data)) return;
          const project = payload?.project;
          if (project === null || project === undefined) {
            setIsExtensionInstalled(false);
            loginStateRef.current.retryCount = 0;
            sendLoginRequest(1000);
          } else if (isMedClipProjectAllowed(project)) {
            setIsExtensionInstalled(true);
          } else {
            setIsExtensionInstalled(false);
          }
        }

        if (eventName === "login.request") {
          if (!isAuthoritativeMedClipMessage(data)) return;
          const state = loginStateRef.current;
          if (success === true) {
            const receivedProject = payload;
            if (receivedProject && isMedClipProjectAllowed(receivedProject)) {
              setIsExtensionInstalled(true);
              state.retryCount = 0;
            } else if (receivedProject === null || receivedProject === undefined) {
              const maxRetries = 3;
              if (state.retryCount < maxRetries) {
                state.retryCount += 1;
                sendLoginRequest(2000 * state.retryCount);
              } else {
                setIsExtensionInstalled(false);
                state.retryCount = 0;
              }
            } else {
              setIsExtensionInstalled(false);
              state.retryCount = 0;
            }
          } else if (success === false || error) {
            setIsExtensionInstalled(false);
            state.retryCount = 0;
            handleMedClipErrorCode(errorCode, error);
          }
        }

        if (eventName === "recording.state.get") {
          const rs = payload?.recordingState;
          if (rs) {
            applyRecordingStateFromExtension(rs);
            handleRecoveredCompletion(rs);
          }
        }

        if (eventName === "recording.start") {
          if (success === true) {
            if (shouldApplyRecordingFlags()) {
              setTimeout(() => requestRecordingState(), 100);
            }
          } else if (success === false || error) {
            if (sessionStudyIdRef.current === studyIdRef.current) {
              sessionStudyIdRef.current = null;
            }
            if (!shouldApplyRecordingFlags()) return;
            if (errorCode === "REC_USER_CANCELED") {
              setRecordingState({ ...IDLE_RECORDING_STATE });
            } else {
              handleMedClipErrorCode(errorCode, error);
              setRecordingState({
                ...IDLE_RECORDING_STATE,
                error: error || "Failed to start recording",
              });
            }
          }
        }

        if (eventName === "recording.stop") {
          if (success === true) {
            if (!shouldApplyRecordingFlags()) return;
            setRecordingState((prev) => ({
              ...prev,
              isRecording: false,
              isPaused: false,
              isStarting: false,
              isLoading: false,
              isProcessing: true,
            }));
          } else if (success === false || error) {
            if (!shouldApplyRecordingFlags()) return;
            if (errorCode !== "REC_NO_RECORDING_IN_PROGRESS") {
              handleMedClipErrorCode(errorCode, error);
            }
            setRecordingState({
              ...IDLE_RECORDING_STATE,
              error: error || null,
            });
          }
        }

        if (eventName === "recording.cancel") {
          if (success === false && errorCode === "REC_CANCEL_FAILED") {
            handleMedClipErrorCode(errorCode, error);
          }
        }

        if (eventName === "record.introductory.video") {
          if (success) {
            toast.success(t("introRecorderOpened") || "Intro recorder opened");
          } else if (error) {
            handleMedClipErrorCode(errorCode, error);
          }
        }
      }

      if (data.type === "push") {
        const { event: eventName, payload } = data;

        if (rejectNonMedClipRecording(eventName)) return;

        if (eventName === "recording.stopped") {
          if (!shouldApplyRecordingFlags()) {
            logIncomingExtensionEvent(data, {
              action: "ignored",
              reason: "recording state applies to another study session",
              currentStudyId: studyIdRef.current,
              sessionStudyId: sessionStudyIdRef.current,
            });
            return;
          }
          setRecordingState((prev) => ({
            ...prev,
            isRecording: false,
            isPaused: false,
            isStarting: false,
            isLoading: false,
            isProcessing: true,
          }));
          requestRecordingState();
        }

        if (eventName === "recording.complete") {
          const payloadStudyId = payload?.studyId;
          const vid = payload?.vid;

          if (payloadStudyId && sessionStudyIdRef.current === payloadStudyId) {
            sessionStudyIdRef.current = null;
          }

          if (!isCompletionForCurrentStudy(payloadStudyId)) {
            logIncomingExtensionEvent(data, {
              action: "ignored",
              reason: "completion for another study",
              currentStudyId: studyIdRef.current,
            });
            return;
          }

          if (shouldSwallowCompletion(payloadStudyId, vid)) {
            logIncomingExtensionEvent(data, {
              action: "swallowed",
              reason: "canceled or duplicate completion",
            });
            return;
          }

          if (vid) {
            handledCompletionVidsRef.current.add(vid);
          }

          setRecordingState({ ...IDLE_RECORDING_STATE });
          onRecordingComplete?.(payload);
        }

        if (eventName === "recording.failed") {
          if (!shouldApplyRecordingFlags()) {
            logIncomingExtensionEvent(data, {
              action: "ignored",
              reason: "recording state applies to another study session",
              currentStudyId: studyIdRef.current,
              sessionStudyId: sessionStudyIdRef.current,
            });
            return;
          }
          const errorMessage = payload?.error || "Recording failed";
          const errorCode = payload?.errorCode || payload?.code;
          sessionStudyIdRef.current = null;
          handleMedClipErrorCode(errorCode, errorMessage);
          setRecordingState({
            ...IDLE_RECORDING_STATE,
            error: errorMessage,
          });
        }

        if (eventName === "recording.canceled") {
          const canceledStudyId = payload?.studyId ?? null;
          if (canceledStudyId && studyIdRef.current && canceledStudyId !== studyIdRef.current) {
            logIncomingExtensionEvent(data, {
              action: "ignored",
              reason: "cancellation for another study",
              currentStudyId: studyIdRef.current,
            });
            return;
          }
          if (canceledStudyId && sessionStudyIdRef.current === canceledStudyId) {
            sessionStudyIdRef.current = null;
          }
          if (canceledStudyId) {
            markStudyCanceled(canceledStudyId);
          }
          if (!shouldApplyRecordingFlags()) return;
          setRecordingState({ ...IDLE_RECORDING_STATE });
        }
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [
    integrationActive,
    sendLoginRequest,
    requestRecordingState,
    applyRecordingStateFromExtension,
    handleRecoveredCompletion,
    handleMedClipErrorCode,
    shouldSwallowCompletion,
    shouldApplyRecordingFlags,
    isCompletionForCurrentStudy,
    markStudyCanceled,
    onRecordingComplete,
    t,
  ]);

  // Mount / study change / focus — probe extension and sync state
  useEffect(() => {
    if (!integrationActive) return undefined;

    if (
      sessionStudyIdRef.current &&
      studyId &&
      sessionStudyIdRef.current !== studyId
    ) {
      setRecordingState(IDLE_RECORDING_STATE);
    }

    probeExtension();

    const onFocus = () => {
      loginStateRef.current.retryCount = 0;
      probeExtension();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [integrationActive, studyId, probeExtension]);

  // Observe medclip-on body class as secondary detection signal
  useEffect(() => {
    if (!integrationActive || typeof document === "undefined") return undefined;
    const syncFromBodyClass = () => {
      if (document.body.classList.contains("medclip-on")) {
        setIsExtensionInstalled(true);
      }
    };
    syncFromBodyClass();
    const observer = new MutationObserver(syncFromBodyClass);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, [integrationActive]);

  useEffect(() => {
    const loginState = loginStateRef.current;
    return () => {
      if (loginState?.timer) {
        clearTimeout(loginState.timer);
        loginState.timer = null;
      }
    };
  }, []);

  const handleStartRecording = useCallback(() => {
    if (!isExtensionInstalled) {
      toast.info(t("installToStartRecording"));
      return;
    }
    const rs = recordingStateRef.current;
    if (rs.isStarting || rs.isProcessing) return;
    sessionStudyIdRef.current = studyIdRef.current ?? null;
    setRecordingState({ ...rs, isStarting: true, error: null });
    postToExtension("recording.start");
  }, [isExtensionInstalled, postToExtension, t]);

  const handleStopRecording = useCallback(() => {
    if (!isExtensionInstalled) return;
    const rs = recordingStateRef.current;
    if (rs.isLoading || (rs.isProcessing && !rs.isRecording)) return;
    setRecordingState({
      ...rs,
      isLoading: true,
      isStarting: false,
      error: null,
    });
    postToExtension("recording.stop");
  }, [isExtensionInstalled, postToExtension]);

  const handleCancelRecording = useCallback(() => {
    if (!isExtensionInstalled) return;
    if (studyId) {
      markStudyCanceled(studyId);
    }
    postToExtension("recording.cancel");
  }, [isExtensionInstalled, postToExtension, studyId, markStudyCanceled]);

  const handleOpenIntroRecorder = useCallback(() => {
    if (!isExtensionInstalled) {
      toast.info(t("installToOpenIntroRecorder"));
      return;
    }
    postToExtension("record.introductory.video");
  }, [isExtensionInstalled, postToExtension, t]);

  return {
    integrationActive,
    isExtensionInstalled,
    showInstallGuide,
    setShowInstallGuide,
    isExtensionSectionDismissed,
    setIsExtensionSectionDismissed,
    recordingState,
    postToExtension,
    requestRecordingState,
    handleStartRecording,
    handleStopRecording,
    handleCancelRecording,
    handleOpenIntroRecorder,
  };
}
